import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import tr from './locales/tr/common.json';
import en from './locales/en/common.json';

/**
 * i18next initialization. Two locales (tr + en) bundled — no runtime
 * network fetch (brief §2.1 forbids them). Default to the user's
 * browser language with `tr` as the fallback (D-028).
 *
 * Catalog completeness is enforced by the `i18n-coverage` test, which
 * walks both files and asserts they expose the same key set.
 */

export const SUPPORTED_LOCALES = ['tr', 'en'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Pick the closest supported locale for a browser-style language tag
 * (e.g. `en-US`, `en`, `tr-TR`). Falls back to `tr` so unknown
 * locales land on the codebase's primary language (D-028). Used by
 * both the boot path here and the LanguageSwitcher display state.
 */
export function resolveLocale(tag: string | undefined | null): Locale {
  const lower = (tag ?? '').toLowerCase();
  for (const loc of SUPPORTED_LOCALES) {
    if (lower === loc || lower.startsWith(loc + '-')) return loc;
  }
  return 'tr';
}

function detectInitialLocale(): Locale {
  if (typeof navigator === 'undefined') return 'tr';
  // We don't use localStorage (brief §5), but Dexie-backed preferences
  // are async; for a synchronous default we read the browser's language
  // and let the app override on mount via setLocale().
  return resolveLocale(navigator.language);
}

void i18n.use(initReactI18next).init({
  resources: {
    tr: { common: tr },
    en: { common: en },
  },
  lng: detectInitialLocale(),
  fallbackLng: 'tr',
  defaultNS: 'common',
  interpolation: {
    escapeValue: false, // React already escapes
  },
  returnNull: false,
});

export default i18n;
